/** Gate de convivência entre a captura legada e a captura local-first. */
import { getExtensionCapabilities, pingExtension } from './extensionBridge';

export type ExtensionFlow = 'unavailable' | 'legacy' | 'local-first';
export type ExtensionConnection = 'checking' | 'connected' | 'unavailable' | 'refresh-required';
export interface ExtensionObservation {
  /** Último fluxo operacional confirmado; preservado durante uma reconexão transitória. */
  flow: ExtensionFlow;
  connection: Exclude<ExtensionConnection, 'checking'>;
}
export const LOCAL_FIRST_CAPABILITY = 'local-first-v1';
let flowInFlight: Promise<ExtensionFlow> | null = null;

function isCapabilityDeclaration(value: unknown): value is { type: 'capabilities'; capabilities: string[] } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    record['type'] === 'capabilities' &&
    Array.isArray(record['capabilities']) &&
    record['capabilities'].every((capability) => typeof capability === 'string')
  );
}

/**
 * Resolve os três estados sem inferir suporte. Uma extensão presente só entra no fluxo novo
 * quando a resposta viva e bem formada declara explicitamente `local-first-v1`.
 */
export function resolveExtensionFlow(extensionAvailable: boolean, declaration: unknown): ExtensionFlow {
  if (!extensionAvailable) return 'unavailable';
  if (!isCapabilityDeclaration(declaration)) return 'legacy';
  return declaration.capabilities.includes(LOCAL_FIRST_CAPABILITY) ? 'local-first' : 'legacy';
}

/**
 * Uma extensão que já respondeu pode perder o content script quando o Chrome instala uma
 * atualização. Nesse intervalo, preservamos o último roteamento somente como memória e
 * bloqueamos novas ações até recarregar; nunca fingimos que ela foi desinstalada.
 */
export function reconcileExtensionObservation(
  previousFlow: ExtensionFlow | null,
  detectedFlow: ExtensionFlow,
): ExtensionObservation {
  if (detectedFlow !== 'unavailable') {
    return { flow: detectedFlow, connection: 'connected' };
  }
  if (previousFlow === 'legacy' || previousFlow === 'local-first') {
    return { flow: previousFlow, connection: 'refresh-required' };
  }
  return { flow: 'unavailable', connection: 'unavailable' };
}

interface CapabilityProbe {
  responded: boolean;
  declaration?: unknown;
}

async function queryCapabilities(timeoutMs: number): Promise<CapabilityProbe> {
  try {
    return {
      responded: true,
      declaration: await getExtensionCapabilities(timeoutMs),
    };
  } catch {
    return { responded: false };
  }
}

/**
 * Pergunta ao código vivo da extensão. Presença (versão/getSync) e capability saem em paralelo,
 * de modo que o fluxo antigo não paga dois timeouts sequenciais. Uma resposta de capability
 * também prova presença; somente `local-first-v1` habilita o protocolo novo.
 */
async function queryExtensionFlowOnce(timeoutMs: number): Promise<ExtensionFlow> {
  const [extensionAvailable, capability] = await Promise.all([
    pingExtension(timeoutMs),
    queryCapabilities(timeoutMs),
  ]);
  return resolveExtensionFlow(
    extensionAvailable || capability.responded,
    capability.declaration,
  );
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

async function queryExtensionFlow(timeoutMs: number): Promise<ExtensionFlow> {
  const first = await queryExtensionFlowOnce(timeoutMs);
  if (first !== 'unavailable') return first;

  // Instalação/update e document_end podem coincidir com o primeiro request. Uma tentativa
  // curta evita transformar esse intervalo em CTA sem criar polling permanente.
  await wait(Math.min(150, Math.max(1, timeoutMs)));
  return queryExtensionFlowOnce(timeoutMs);
}

/** Compartilha o handshake vivo enquanto uma consulta está em voo; a próxima chamada após a
 * conclusão consulta novamente para continuar cobrindo update/downgrade com o painel aberto. */
export function getExtensionFlow(timeoutMs = 1200): Promise<ExtensionFlow> {
  if (flowInFlight) return flowInFlight;

  const request = queryExtensionFlow(timeoutMs);
  flowInFlight = request;
  void request.then(
    () => {
      if (flowInFlight === request) flowInFlight = null;
    },
    () => {
      if (flowInFlight === request) flowInFlight = null;
    },
  );
  return request;
}
